import { createElement, FC, useState } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Button, Col, Divider, Popconfirm, Row, Typography } from 'antd';
import {
    TbCircleCheckFilled,
    TbExclamationCircleFilled,
    TbPuzzle,
    TbRosetteDiscount,
    TbSquareRoundedMinus,
    TbSquareRoundedPlus,
} from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { FeatureAddOnConfig } from '@/constants';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useBillingSnapshot } from '@/hooks/useBillingSnapshot';
import { MiddleDot, OurDocsLink, SmallTag } from '@/shared';
import { PlatformAlert } from '@/shared/components/alerts';
import { BaseSkeleton, ButtonSkeleton } from '@/shared/components/skeletons';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { hasFeatureAddOnAccess, isPlanEntitlementOverrideApplied } from '@/shared/entitlements';
import { useUserStore } from '@/stores';
import { useBillingStore } from '@/stores/database/aliases/billing';
import { formatDate } from '@/utilities';

import EnableFeatureAddOnModal from './EnableFeatureAddOnModal';

interface FeatureAddOnCardProps {
    fao: FeatureAddOnConfig;
}

const FeatureAddOnCard: FC<FeatureAddOnCardProps> = ({ fao }): JSX.Element => {
    const { selectedUser } = useUserStore();

    const { token } = useAntDesignTheme();

    const [open, setOpen] = useState(false);

    const { snapshot } = useBillingSnapshot();
    const { refreshSnapshot, loading } = useBillingStore();

    const isEnabled = hasFeatureAddOnAccess(snapshot, fao.key);
    const includedByPlan = isPlanEntitlementOverrideApplied(snapshot);

    const [confirmLoading, setConfirmLoading] = useState(false);

    const handleDisableFeatureAddOn = async () => {
        try {
            setConfirmLoading(true);

            await authorizedAxiosInstance.post(`/api/billing/feature-add-ons/disable`, { featureKey: fao.key });

            setOpen(false);

            toast('Add-On Disabled', {
                icon: <TbPuzzle size={'1rem'} />,
                description: fao.label,
            });

            await refreshSnapshot();
        } catch (err) {
            captureException(err);

            toast.error('Unable to Disable Add-on', { description: 'Please try again' });
        } finally {
            setConfirmLoading(false);
        }
    };

    const filteredFeatureAddOn = snapshot?.addOns?.filter((sFAO) => sFAO?.key === fao?.key)[0];

    return (
        <>
            <div
                style={{
                    marginBottom: '1rem',
                    border: `1px solid ${token.colorBorder}`,
                    borderRadius: token.borderRadius,
                    overflow: 'auto',
                }}
            >
                {!loading ? (
                    <>
                        {isEnabled && (
                            <div style={{ position: 'relative' }}>
                                <PlatformAlert
                                    banner
                                    message={includedByPlan ? 'Included with Enterprise' : 'Add-On Enabled'}
                                    type='success'
                                    style={{ overflow: 'auto' }}
                                    icon={<TbCircleCheckFilled />}
                                    action={
                                        includedByPlan ? (
                                            <small style={{ verticalAlign: '1.5px' }}>
                                                Available throughout this workspace
                                            </small>
                                        ) : snapshot?.seats?.activeSeats ? (
                                            <small style={{ verticalAlign: '1.5px' }}>
                                                Enabled in Workspace{' '}
                                                {filteredFeatureAddOn?.enabledAt &&
                                                    formatDate(new Date(filteredFeatureAddOn?.enabledAt))}
                                            </small>
                                        ) : null
                                    }
                                />
                                <span
                                    aria-hidden='true'
                                    style={{
                                        background: `linear-gradient(to right, ${token.colorSuccessBorder} 0%, ${token.colorBorder} 100%)`,
                                        bottom: 0,
                                        height: 1,
                                        left: 0,
                                        pointerEvents: 'none',
                                        position: 'absolute',
                                        right: 0,
                                        zIndex: 2,
                                    }}
                                />
                            </div>
                        )}
                        <div style={{ padding: '1rem' }}>
                            <Row key={fao?.key} style={{ width: '100%' }} gutter={[16, 0]} wrap={false} align={'top'}>
                                <Col flex='auto'>
                                    <Typography.Title level={5}>
                                        <Avatar
                                            size={24}
                                            shape='square'
                                            icon={createElement(fao?.icon, {
                                                style: { color: token.colorTextTertiary },
                                                size: '67.5%',
                                            })}
                                            style={{
                                                border: `1px solid ${token.colorBorder}`,
                                                marginRight: '0.5rem',
                                                verticalAlign: '-1px',
                                            }}
                                        />
                                        {fao?.label}
                                    </Typography.Title>
                                </Col>
                            </Row>
                            <Typography.Paragraph style={{ opacity: '50%' }}>{fao?.description}</Typography.Paragraph>
                            <Row>
                                {/* <Col flex='none'>
                                    <OurDocsLink text={'Demo Video'} />
                                </Col> */}
                                {/* <Col flex='auto' /> */}
                                <Col flex='none'>
                                    <OurDocsLink text={'Documentation'} />
                                </Col>
                            </Row>
                            {!isEnabled ? (
                                <>
                                    <Divider style={{ margin: '1rem 0' }} />
                                    <Row style={{ marginBottom: '1rem' }} align={'middle'} wrap={false}>
                                        <Col flex='auto'>
                                            <small style={{ color: token.colorTextTertiary, fontWeight: 400 }}>
                                                $
                                                <span style={{ fontWeight: 600, color: token.colorTextSecondary }}>
                                                    {fao?.pricing?.monthly?.unitAmount}
                                                </span>
                                                /active seat/month
                                                <MiddleDot horizontalMargin={'0.5rem'} />$
                                                <span style={{ fontWeight: 600, color: token.colorTextSecondary }}>
                                                    {fao?.pricing?.annual?.yearlyAmount}
                                                </span>
                                                /active seat/year
                                            </small>
                                        </Col>
                                        <Col flex='none'>
                                            <SmallTag
                                                message={
                                                    <>
                                                        <TbRosetteDiscount
                                                            style={{
                                                                verticalAlign: '-0.1rem',
                                                                marginRight: '0.125rem',
                                                            }}
                                                        />
                                                        20% Off Annual Plans
                                                    </>
                                                }
                                                styles={{ backgroundColor: token.green2, color: token.green7 }}
                                            />
                                        </Col>
                                    </Row>
                                    <Button
                                        block
                                        onClick={() => setOpen(true)}
                                        type='dashed'
                                        disabled={!snapshot?.entitlements?.canUseAddOns}
                                    >
                                        <TbSquareRoundedPlus />
                                        Enable Add-On
                                    </Button>
                                </>
                            ) : !includedByPlan ? (
                                <>
                                    <Divider style={{ margin: '1rem 0' }} />
                                    <Row style={{ marginBottom: '1rem' }} align={'middle'} wrap={false}>
                                        <Col flex='auto'>
                                            <small style={{ opacity: '50%', fontWeight: 400 }}>
                                                $<strong>{fao?.pricing?.monthly?.unitAmount}</strong>/active seat/month
                                                or $<strong>{fao?.pricing?.annual?.yearlyAmount}</strong>
                                                /active seat/year
                                            </small>
                                        </Col>
                                        <Col flex='none'>
                                            <SmallTag
                                                message={
                                                    <>
                                                        <TbRosetteDiscount
                                                            style={{
                                                                verticalAlign: '-0.1rem',
                                                                marginRight: '0.125rem',
                                                            }}
                                                        />
                                                        20% Off Annual Plans
                                                    </>
                                                }
                                                styles={{ backgroundColor: token.green2, color: token.green7 }}
                                            />
                                        </Col>
                                    </Row>
                                    <Popconfirm
                                        title='Are you sure you want to disable this add-on? You will lose access to features included in the add-on at the end of the billing cycle.'
                                        onConfirm={handleDisableFeatureAddOn}
                                        placement='leftTop'
                                        styles={{ body: { width: 16 * 16, marginRight: '1rem' } }}
                                        okText='Yes'
                                        okButtonProps={{ disabled: !selectedUser?.stripeId, loading: confirmLoading }}
                                        cancelText='No'
                                        arrow={false}
                                        icon={
                                            <TbExclamationCircleFilled
                                                size='1rem'
                                                style={{
                                                    color: token.gold6,
                                                    verticalAlign: '-0.125rem',
                                                    marginRight: '0.5rem',
                                                }}
                                            />
                                        }
                                    >
                                        <Button block type='dashed' danger>
                                            <TbSquareRoundedMinus />
                                            Disable Add-On
                                        </Button>
                                    </Popconfirm>
                                </>
                            ) : null}
                            <EnableFeatureAddOnModal open={open} setOpen={setOpen} featureAddOn={fao} />
                        </div>
                    </>
                ) : (
                    <div style={{ padding: '1rem' }}>
                        <ButtonSkeleton block active />
                        <BaseSkeleton round active paragraph={{ rows: 1 }} />
                        <ButtonSkeleton block active style={{ height: '1rem', marginBottom: '1rem' }} />
                        <Divider style={{ margin: '1rem 0', marginTop: 0 }} />
                        <ButtonSkeleton block active style={{ height: '1rem', marginBottom: '1rem' }} />
                        <ButtonSkeleton block active />
                    </div>
                )}
            </div>
        </>
    );
};

export default FeatureAddOnCard;
